import { Veglenkesekvensposisjon } from '@/domain/Veglenkesekvensposisjon';
import { VegsystemreferanseLookup } from '@/state';
import { transformGeometri } from './geometri';
import type {
    Geometri as ApiGeometri,
    Kryssystem as ApiKryssystem,
    MetrertLokasjon as ApiMetrertLokasjon,
    Sideanlegg as ApiSideanlegg,
    Strekning as ApiStrekning,
    Vegsystem as ApiVegsystem,
    Vegsystemreferanse as ApiVegsystemreferanse,
} from '@/generated/nvdb/vegobjekter/models';
import {
    Vegposisjon,
    Kryssystem,
    Sideanlegg,
    Strekning,
    Vegsystem,
    Vegsystemreferanse,
    MetrertLokasjon,
} from '@/domain/omrader';
import { getPointFromWKT } from '@/utils/WKT';

type RoadReferenceLookupResponse = {
    vegsystemreferanse?: ApiVegsystemreferanse;
    veglenkesekvens: {
        veglenkesekvensid: number;
        relativPosisjon: number;
        kortform: string;
    };
    geometri: ApiGeometri;
    avstand: number;
    kommune: number;
    code?: number;
};

export function transformVegsystemreferanse(
    vegsysrefData: ApiVegsystemreferanse | null | undefined,
    kommune?: number
): Vegsystemreferanse | null {
    if (!vegsysrefData || Object.keys(vegsysrefData).length === 0) return null;

    const vegsystem = requireValue(vegsysrefData.vegsystem, 'vegsystem');

    return new Vegsystemreferanse(
        transformVegsystem(vegsystem),
        transformStrekning(vegsysrefData.strekning) as Strekning,
        transformKryssystem(vegsysrefData.kryssystem),
        transformSideanlegg(vegsysrefData.sideanlegg),
        transformMetrertLokasjon(vegsysrefData.metrertLokasjon),
        transformKortform(vegsysrefData.kortform ?? '', vegsystem.vegkategori, kommune)
    );
}

function requireValue<T>(value: T | undefined, fieldName: string): NonNullable<T> {
    if (value === undefined || value === null) {
        throw new Error(`Vegsystemreferanse mangler ${fieldName}`);
    }

    return value as NonNullable<T>;
}

function transformKortform(
    kortform: string,
    vegkategori: string,
    kommune?: number
): string {
    return ['K', 'P', 'S'].includes(vegkategori) && kommune != null
        ? `${kommune} ${kortform}`
        : kortform;
}

function transformVegsystem(vegsystemData: ApiVegsystem): Vegsystem {
    return new Vegsystem(
        vegsystemData.vegkategori,
        vegsystemData.fase,
        vegsystemData.nummer ?? null
    );
}

function transformStrekning(strekningData: ApiStrekning | undefined): Strekning | null {
    return strekningData
        ? new Strekning(
              new Vegposisjon(strekningData.strekning),
              strekningData.delstrekning
                  ? new Vegposisjon(strekningData.delstrekning)
                  : null,
              strekningData.arm,
              strekningData.adskilte_løp,
              strekningData.adskilte_løp_nummer
                  ? Number(strekningData.adskilte_løp_nummer)
                  : null,
              strekningData.trafikantgruppe,
              strekningData.meter != null
                  ? new Vegposisjon(strekningData.meter)
                  : new Vegposisjon(
                        requireValue(strekningData.fra_meter, 'strekning.fra_meter'),
                        strekningData.til_meter
                    ),
              strekningData.retning
          )
        : null;
}

function transformKryssystem(kryssData: ApiKryssystem | undefined): Kryssystem | null {
    if (!kryssData) return null;

    return new Kryssystem(
        kryssData.kryssystem,
        new Vegposisjon(kryssData.kryssdel),
        kryssData.trafikantgruppe,
        kryssData.meter != null
            ? new Vegposisjon(kryssData.meter)
            : new Vegposisjon(
                  requireValue(kryssData.fra_meter, 'kryssystem.fra_meter'),
                  kryssData.til_meter
              ),
        kryssData.retning
    );
}

function transformSideanlegg(sideData: ApiSideanlegg | undefined): Sideanlegg | null {
    if (!sideData) return null;

    return new Sideanlegg(
        sideData.sideanlegg,
        new Vegposisjon(sideData.sideanleggsdel),
        sideData.trafikantgruppe,
        sideData.meter != null
            ? new Vegposisjon(sideData.meter)
            : new Vegposisjon(
                  requireValue(sideData.fra_meter, 'sideanlegg.fra_meter'),
                  sideData.til_meter
              ),
        sideData.retning
    );
}

function transformMetrertLokasjon(
    metrertLokasjonData: ApiMetrertLokasjon | undefined
): MetrertLokasjon | null {
    return metrertLokasjonData
        ? new MetrertLokasjon(
              metrertLokasjonData.retning ?? null,
              metrertLokasjonData.sideposisjon ?? null,
              metrertLokasjonData.kjørefelt ?? null
          )
        : null;
}

export function transformRoadReferenceLookup(
    data: RoadReferenceLookupResponse | RoadReferenceLookupResponse[]
): VegsystemreferanseLookup | null {
    const responseData = requireValue(
        data instanceof Array ? data[0] : data,
        'oppslagsrespons'
    );
    const { vegsystemreferanse, veglenkesekvens, geometri, avstand, code } = responseData;
    const kommune = responseData.kommune;
    if (!!code) {
        // error code
        return null;
    }

    const { wkt } = geometri;
    const geo = getPointFromWKT(wkt);
    return new VegsystemreferanseLookup(
        requireValue(geo.at(0), 'geometri.east'),
        requireValue(geo.at(1), 'geometri.north'),
        transformVegsystemreferanse(vegsystemreferanse, kommune) ?? undefined,
        new Veglenkesekvensposisjon(
            veglenkesekvens.veglenkesekvensid,
            veglenkesekvens.relativPosisjon,
            veglenkesekvens.kortform
        ),
        transformGeometri(geometri),
        avstand,
        kommune
    );
}
